import { createElement as rc, useRef, useEffect, useState } from 'react';
import log from '@sstdev/lib_logging';
import { Card, Modal, getQueue, FeedbackIcon } from 'lib_ui-primitives';
import useEventSink from '../../hooks/useEventSink';
import { constants } from 'lib_ui-services';

import { ConfirmationCard, CardHeader, HeaderText, Body, Content, ButtonBar, DialogButton } from './styles';

const { VARIANT } = constants;

const defaultDialogInfo = {
    message: '',
    okButtonText: 'OK',
    cancelButtonText: 'CANCEL',
    okAction: () => {},
    cancelAction: () => {},
    noCancelOption: false,
    jumpQueue: false
};

const variantIconMap = {
    [VARIANT.Info]: 'zap',
    [VARIANT.Warning]: 'alert-triangle',
    [VARIANT.Error]: 'alert-circle',
    [VARIANT.Success]: 'check-circle'
};

const iconMap = {
    warning: 'alert-triangle',
    error: 'alert-circle',
    info: 'zap',
    success: 'check-circle'
};

function ConfirmationDialog() {
    const [subscribe] = useEventSink();
    const [dialogInfo, setDialogInfo] = useState(defaultDialogInfo);
    const [visible, setVisible] = useState(false);
    // If more than one confirmation is requested, then queue them.
    // Note: this is a separate queue from the modal queue. This one only controls the content displayed INSIDE the confirmation dialog
    const queue = useRef(getQueue('ConfirmationDialog')).current;

    // When a new confirmation reaches the front of the queue, display it.
    useEffect(() => {
        queue.onNewLeader(newLeader => {
            if (newLeader != null) {
                setDialogInfo(newLeader);
                setVisible(true);
            }
        });
    }, [queue]);

    // Listen for confirmation dialog requests and queue them
    useEffect(() => {
        let openActionType = { verb: 'confirm', namespace: 'application', relation: 'user' };
        const unsubscribes = [
            subscribe(openActionType, payload => {
                const dialogInfo = { ...defaultDialogInfo, ...payload };
                if (dialogInfo.jumpQueue) {
                    queue.jumpQueue(dialogInfo);
                } else {
                    queue.enqueue(dialogInfo);
                }
            })
        ];
        // unsubscribe during cleanup
        return () =>
            unsubscribes.forEach(u => {
                u();
            });
    }, [subscribe, queue]);

    const {
        title: defaultTitle,
        message,
        okButtonText,
        cancelButtonText,
        okAction,
        cancelAction,
        noCancelOption,
        icon,

        /** @Deprecated */
        iconColorStyle,
        variant: _variant,
        jumpQueue = false
    } = dialogInfo;

    const title = defaultTitle ?? (noCancelOption ? 'Please Be Advised' : 'Please Confirm');
    let variant = _variant;
    if (!_variant && iconColorStyle) {
        log.warn('DEPRECATION WARNING: iconColorStyle is deprecated. Please use variant instead.');
        variant = iconColorStyle;
    } else {
        variant = variant || 'info';
    }
    let color = 'primary';
    if ([VARIANT.Error, VARIANT.Warning, VARIANT.Success].includes(variant)) {
        color = variant;
    }

    function onCancel() {
        setVisible(false);
        setDialogInfo(defaultDialogInfo);
        cancelAction();
        queue.next();
    }
    function onOk() {
        setVisible(false);
        setDialogInfo(defaultDialogInfo);
        okAction();
        queue.next();
    }

    let messageAsArray = [].concat(message);

    // prettier-ignore
    return rc(Modal, { id: 'confirmationDialogModal', visible, jumpQueue },
        rc(ConfirmationCard, null,
            rc(CardHeader, null,
                rc(FeedbackIcon,
                    {
                        color,
                        variant: 'primary',
                        size: 'lg',
                        name: icon? iconMap[icon] || icon : variantIconMap[variant] || 'zap',
                        key: '0'
                    }
                ),
                rc(HeaderText, null, title)
            ),
            rc(Body, null,
                messageAsArray.map((line, i) => rc(Content, { key: i }, line))
            ),
            rc(Card.Footer, null, rc(ButtonBar, null,
                !noCancelOption && rc(DialogButton, { value: cancelButtonText, onClick: onCancel, variant: 'secondary', size:'lg' }),
                rc(DialogButton, { value: okButtonText, onClick: onOk, variant: 'primary', destructive: variant === VARIANT.Error, size:'lg' })
            ))
        )
    );
}

export default ConfirmationDialog;
